import { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api.js';

/**
 * Full-screen modal that previews a saved document inline so the tech never
 * leaves the troubleshooting flow to read a tech sheet or wiring diagram.
 *
 * - PDFs render every page stacked vertically using pdfjs-dist (already a
 *   dependency, same loader pattern as DocPageThumb). Pages render at
 *   devicePixelRatio × 1.2 for crisp text on Retina iPad displays.
 * - Images use a plain <img>; iPad Safari handles pinch-to-zoom natively
 *   when `touch-action: pinch-zoom` is set.
 * - Anything else falls through to the "Open in new tab" escape hatch in
 *   the top bar so the tech is never stuck.
 *
 * Body scroll on iPad is locked while the modal is open so a flick on the
 * doc doesn't accidentally pan the page underneath.
 */
export default function DocViewer({ doc, onClose, initialPage }) {
  // Lock background scroll while the modal is mounted
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape key closes (useful on Mac browsers; harmless on iPad)
  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!doc) return null;
  const kind = detectKind(doc);
  const fileUrl = api.documentFileUrl(doc.id);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={`Document viewer: ${doc.file_name || 'document'}`}
    >
      {/* Top bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-ink-800 bg-ink-900 shrink-0">
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate text-sm sm:text-base">
            {doc.file_name || 'Document'}
          </div>
          <div className="text-ink-400 text-xs">
            {kindLabel(kind)}{doc.doc_type ? ` · ${docTypeLabel(doc.doc_type)}` : ''}
          </div>
        </div>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 min-h-[44px] px-3 inline-flex items-center text-sm text-mra-red hover:text-white transition whitespace-nowrap"
        >
          Open in new tab
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close viewer"
          className="shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-md text-ink-300 hover:text-white active:bg-ink-800 transition"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto bg-ink-950">
        {kind === 'pdf' && <PdfBody docId={doc.id} initialPage={initialPage} />}
        {kind === 'image' && <ImageBody fileUrl={fileUrl} />}
        {kind === 'unknown' && <UnknownBody />}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PDF rendering — fetches once, then renders each page in its own component
// so React owns the canvas refs cleanly (no imperative DOM mutation).

function PdfBody({ docId, initialPage }) {
  const [pdf, setPdf]               = useState(null);
  const [pageCount, setPageCount]   = useState(0);
  // 'loading' | 'ready' | 'error'
  const [status, setStatus]         = useState('loading');
  const [errorMsg, setErrorMsg]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pdfInstance = null;

    async function load() {
      setStatus('loading');
      try {
        const url = api.documentFileUrl(docId);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buf = await response.arrayBuffer();
        if (cancelled) return;

        const [pdfjs, workerMod] = await Promise.all([
          import('pdfjs-dist'),
          import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]);
        if (cancelled) return;
        pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default;

        pdfInstance = await pdfjs.getDocument({ data: buf }).promise;
        if (cancelled) {
          pdfInstance.destroy?.();
          return;
        }
        setPdf(pdfInstance);
        setPageCount(pdfInstance.numPages);
        setStatus('ready');
      } catch (err) {
        console.warn('PDF viewer error:', err);
        if (!cancelled) {
          setErrorMsg(err?.message || 'Failed to load PDF');
          setStatus('error');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      pdfInstance?.destroy?.();
    };
  }, [docId]);

  if (status === 'loading') {
    return (
      <div className="text-center py-16 text-ink-300">
        <div className="inline-block animate-spin h-8 w-8 border-2 border-ink-600 border-t-mra-red rounded-full mb-3" />
        <div className="text-sm">Loading PDF…</div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="card border-fail bg-fail/10 max-w-md mx-auto mt-8">
        <p className="text-white">⚠ Could not load this PDF inline.</p>
        {errorMsg && <p className="text-ink-300 text-sm mt-2 break-words">{errorMsg}</p>}
        <p className="text-ink-300 text-sm mt-3">
          Tap <span className="text-mra-red">Open in new tab</span> at the top to view it.
        </p>
      </div>
    );
  }

  return (
    <div className="px-3 py-4 space-y-6 max-w-5xl mx-auto">
      {Array.from({ length: pageCount }, (_, i) => (
        <PdfPage
          key={i + 1}
          pdf={pdf}
          pageNum={i + 1}
          totalPages={pageCount}
          scrollIntoView={initialPage != null && i + 1 === Math.min(Math.max(1, initialPage), pageCount)}
        />
      ))}
    </div>
  );
}

// iOS Safari enforces a hard budget on total live canvas memory; a big service
// manual rendered every page at DPR×1.2 blows straight through it (silently
// blank canvases, or the whole tab killed mid-diagnosis). Two defenses:
//   1. Cap any single page's bitmap at ~4.2M pixels (still crisp for print).
//   2. Render lazily — only pages near the viewport hold a live bitmap;
//      far-offscreen pages release theirs and re-render on the way back.
const MAX_RENDER_PIXELS = 4_200_000;

function PdfPage({ pdf, pageNum, totalPages, scrollIntoView = false }) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [failed, setFailed] = useState(false);
  // Page aspect (height/width) from a metadata-only pass — sizes the
  // placeholder so scroll position and scroll-to-page hold before rendering.
  const [aspect, setAspect] = useState(null);
  const [visible, setVisible] = useState(
    typeof IntersectionObserver === 'undefined'
  );
  const didScrollRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!pdf) return;
    pdf
      .getPage(pageNum)
      .then((page) => {
        if (cancelled) return;
        const vp = page.getViewport({ scale: 1 });
        setAspect(vp.height / vp.width);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pdf, pageNum]);

  // Track proximity to the viewport. The generous rootMargin pre-renders a
  // couple of screens ahead so normal scrolling never shows a blank page.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: '2400px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // When this is the page the caller asked to open to (e.g. a cited manual
  // page tapped from the troubleshoot transcript), bring it into view as soon
  // as the placeholder has its real proportions. Guarded to fire once.
  useEffect(() => {
    if (
      scrollIntoView &&
      !didScrollRef.current &&
      wrapperRef.current &&
      (aspect != null || rendered)
    ) {
      didScrollRef.current = true;
      wrapperRef.current.scrollIntoView({ block: 'start' });
    }
  }, [scrollIntoView, aspect, rendered]);

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;

    if (!visible) {
      // Far offscreen: release the bitmap (a 0×0 canvas frees its memory).
      if (canvas && canvas.width > 0) {
        canvas.width = 0;
        canvas.height = 0;
        setRendered(false);
      }
      return;
    }

    async function render() {
      if (!pdf) return;
      try {
        const page = await pdf.getPage(pageNum);
        if (cancelled) return;

        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) return;

        // Render at the wrapper's CSS width, scaled up by DPR for sharpness on
        // Retina iPads, then scaled down with a CSS width so the canvas displays
        // at its intended size while the bitmap stays high-res — clamped so no
        // single page exceeds the pixel budget.
        const containerWidth = wrapper.clientWidth;
        const dpr = window.devicePixelRatio || 1;
        const baseViewport = page.getViewport({ scale: 1 });
        const fitScale = containerWidth / baseViewport.width;
        const pixelCap = Math.sqrt(
          MAX_RENDER_PIXELS / (baseViewport.width * baseViewport.height)
        );
        const renderScale = Math.min(fitScale * dpr * 1.2, pixelCap);
        const viewport = page.getViewport({ scale: renderScale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
        }).promise;
        if (!cancelled) setRendered(true);
      } catch (err) {
        // Parent's PDF instance can be destroyed mid-render (unmount, or a
        // render task pdf.js cancels on purpose) — neither is a real failure.
        if (cancelled || err?.name === 'RenderingCancelledException') return;
        console.warn(`PDF page ${pageNum} render error:`, err);
        setFailed(true);
      }
    }

    render();
    return () => { cancelled = true; };
  }, [pdf, pageNum, visible]);

  return (
    <div ref={wrapperRef}>
      <div className="text-ink-400 text-xs font-mono uppercase tracking-wider text-center mb-2">
        Page {pageNum} of {totalPages}
      </div>
      {failed ? (
        <div className="card border-fail bg-fail/10 max-w-md mx-auto">
          <p className="text-white text-sm">⚠ Couldn't render page {pageNum}.</p>
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          className="block bg-white rounded-md shadow-lg mx-auto"
          style={{
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            // Holds the page's footprint while the bitmap is released, so the
            // scrollbar doesn't jump as pages render/release around the tech.
            aspectRatio: `1 / ${aspect || 1.294}`,
            opacity: rendered ? 1 : 0.25,
            transition: 'opacity 200ms',
          }}
          aria-label={`Page ${pageNum}`}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Image rendering — Safari handles pinch-zoom natively when touch-action is
// set; no custom gesture code needed.

function ImageBody({ fileUrl }) {
  return (
    <div className="flex items-center justify-center p-3 min-h-full">
      <img
        src={fileUrl}
        alt=""
        className="max-w-full rounded-md shadow-lg"
        style={{ touchAction: 'pinch-zoom', maxHeight: 'calc(100vh - 80px)' }}
      />
    </div>
  );
}

function UnknownBody() {
  return (
    <div className="card max-w-md mx-auto mt-8 text-center">
      <p className="text-white">This file type can't be previewed in the app.</p>
      <p className="text-ink-300 text-sm mt-2">
        Tap <span className="text-mra-red">Open in new tab</span> at the top to view it.
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers

export function detectKind(doc) {
  if (!doc) return 'unknown';
  const mt = (doc.mime_type || '').toLowerCase();
  if (mt.includes('pdf')) return 'pdf';
  if (mt.startsWith('image/')) return 'image';
  const name = (doc.file_name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?)$/i.test(name)) return 'image';
  return 'unknown';
}

function kindLabel(kind) {
  return { pdf: 'PDF', image: 'Image', unknown: 'Document' }[kind] || 'Document';
}

function docTypeLabel(t) {
  return {
    tech_sheet:     'Tech Sheet',
    wiring_diagram: 'Wiring Diagram',
    both:           'Tech Sheet + Diagram',
    other:          'Other',
  }[t] || t;
}

function CloseIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
